
import { AppointmentService } from 'src/app/core/service/appointment.service';
import { Component, OnInit, Input, ChangeDetectorRef, OnDestroy, AfterViewChecked, ElementRef, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { EventClickEvent, RemoveEvent, DateChangeEvent, SchedulerEvent, SlotClickEvent } from '@progress/kendo-angular-scheduler';
import { State } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import { EventData } from 'src/app/event/model/event';
import { SettingService } from 'src/app/core/service/setting.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import moment from 'moment';
@Component({
  selector: 'app-event-scheduler',
  templateUrl: './event-scheduler.component.html',
  styleUrls: ['./event-scheduler.component.scss']
})
export class EventSchedulerComponent implements OnInit, OnDestroy, AfterViewChecked
{

  @Input() public memberId: number;
  @Output() public refresh = new EventEmitter();

  public selectedDate: Date = new Date();
  public events: SchedulerEvent[] = [];
  public formGroup: UntypedFormGroup;
  public selectedStartDate: Date;
  public selectedEndDate: Date;

  // ResourcesData is being used to change the item color to indicate a MISSED vs a SCHEDULE appointment
  public resourcesData = [
    {
      id: 'SCHEDULED',
      color: '#6f899b',
    },
    {
      id: 'MISSED',
      color: '#e96b62',
    },
    {
      id: 'CANCELLED',
      color: '#e96b62',
    },
    {
      id: 'CONFIRMED',
      color: '#7d9550',
    }
  ];

  public resources = [
    {
      colorField: 'color',
      data: this.resourcesData,
      field: 'dataItem.appointment.status',
      valueField: 'id'
    }
  ];

  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public defaultViewIndex = -1;
  public isNew: boolean;
  public editDataItem: any;
  public cancelDataItem: any;
  public sessionSettings: SessionSetting;

  private listSubscription: Subscription;
  private enabledViews: string;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private eventListService: EventListService,
    private settingService: SettingService,
    private appointmentService: AppointmentService,
    private elRef: ElementRef)
  {
  }

  public get loading(): boolean
  {
    return this.eventListService.loading;
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.sessionSettings = sessionSetting;
        this.setDefaultViewIndex(sessionSetting.schedulerViews);
        this.listSubscription = this.eventListService.subscribe({
          next: (value: EventData) =>
          {
            // Preprocessing data before it's passed to the Kendo Grid.
            if (value !== null)
            {
              value.data.forEach(d =>
              {
                d.eventDateTime = new Date(d.eventDateTime);
                d.eventEndDateTime = new Date(d.eventEndDateTime);
                d.eventTypeIcon = this.eventListService.getEventTypeIcon(d.eventType);
                d.eventTypeText = this.eventListService.getEventTypeText(d.eventType);
                d.encounterTypeText = this.eventListService.getEncounterTypeText(d.encounterType);
                if (d.appointment !== null && d.appointment !== undefined)
                {
                  d.appointment.statusText = this.eventListService.getAppointmentStatusText(d.appointment.status);
                }
              }
              );
            }
            this.convertEventsDataToArray(value);
          }
        });
      }
    });
  }

  public ngAfterViewChecked(): void
  {
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  public onDateChange(args: DateChangeEvent): void
  {
    // If defaultViewIndex is not set yet, do not get events. UI need to render before.
    if (this.defaultViewIndex === -1)
    {
      return;
    }
    this.selectedStartDate = args.dateRange.start;
    this.selectedEndDate = args.dateRange.end;
    this.refresh.emit();
    this.getEvents(this.memberId, args.dateRange.start, args.dateRange.end);
  }

  public isViewEnabled(view: string): boolean
  {
    const views = this.enabledViews.split(',');
    let found = false;
    views.forEach(v =>
    {
      if (v === view)
      {
        found = true;
        return;
      }
    });
    return found;
  }

  /**
   * Adding an event
   */
  public slotDblClickHandler({ start, end, isAllDay }: SlotClickEvent): void
  {
    const isFutureDate = moment(start).diff(moment()) > 0;
    this.isNew = true;

    const editData: any = {};
    editData.title = '';
    editData.description = '';
    editData.start = isFutureDate ? start : null;
    editData.end = isFutureDate ? (isAllDay ? start : end) : null;
    editData.isAllDay = isAllDay;

    this.editDataItem = editData;
  }

  /**
   * Editing an event
   */
  public eventDblClickHandler({ event }: EventClickEvent): void
  {
    this.isNew = false;
    this.editDataItem = event.dataItem;
  }

  public removeHandler({ dataItem }: RemoveEvent): void
  {
    this.cancelDataItem = dataItem;
  }

  public saveChangesHandler(): void
  {
    this.editDataItem = undefined;
    this.refresh.emit();
    this.getEvents(this.memberId, this.selectedStartDate, this.selectedEndDate);
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  public saveCancelHandler(): void
  {
    this.cancelDataItem = undefined;
    this.refresh.emit();
    this.getEvents(this.memberId, this.selectedStartDate, this.selectedEndDate);
  }

  public cancelCancelHandler(): void
  {
    this.cancelDataItem = undefined;
  }

  public getViewText(view: string): string
  {
    return this.appointmentService.getSchedulerViewTypeText(view);
  }

  private setDefaultViewIndex(views: string): void
  {
    this.enabledViews = views;
    setTimeout(() =>
    {
      // Set proper index to either WORK_WEEK, WEEK or the first item in the view list
      let index = this.findViewIndex('WORK_WEEK');
      if (index !== -1)
      {
        this.defaultViewIndex = index;
      }
      else
      {
        index = this.findViewIndex('WEEK');
        if (index !== -1)
        {
          this.defaultViewIndex = index;
        }
      }
      if (this.defaultViewIndex === -1)
      {
        this.defaultViewIndex = 0;
      }
    });
  }

  /**
   * Method that locate the rendered enabled views to find what it the right index to use.
   *
   * @param viewValue View text value: Week or Work Week. It compared with the innerText of the rendered html for the component.
   */
  private findViewIndex(viewValue: string): number
  {
    let foundIndex = -1;
    const viewElements = this.elRef.nativeElement.querySelectorAll('.k-scheduler-views li');
    const schedulerView = this.appointmentService.schedulerViews.find(x => x.value === viewValue);
    if (schedulerView !== undefined)
    {
      let i = 0;
      viewElements.forEach(view =>
      {
        const items = view.querySelectorAll('span');
        items.forEach((item: HTMLSpanElement) =>
        {
          if (item.innerText === schedulerView.text)
          {
            foundIndex = i - 1;
            return;
          }
        });
        i++;
      });
    }
    return foundIndex;
  }

  /**
   * This method transform the event data into a kendo-scheduler event array
   *
   * @param data Event data loaded
   */
  private convertEventsDataToArray(events: EventData): void
  {
    this.events = [];

    if (events !== null)
    {
      // Not using timezone, stored as current browser timezone for v1.
      const loadedEvents = events.data.map(e => ({
        id: e.id,
        title: e.appointment && e.appointment.title ? e.appointment.title : '',
        description: e.details,
        startTimezone: null,
        start: e.eventDateTime,
        endTimezone: null,
        end: e.eventEndDateTime,
        recurrenceRule: null,
        recurrenceId: null,
        recurrenceExceptions: null,
        isAllDay: e.appointment && e.appointment.allDay ? e.appointment.allDay : false,
        dataItem: e
      }));
      this.events = loadedEvents;
    }
  }

  /**
   * Get events from API per the applied filter.
   *
   * @param filter Event filter string.
   */
  private getEvents(memberId: number, fromDate: Date, toDate: Date): void
  {
    this.state = {
      filter: {
        filters: [
          { field: 'memberId', operator: 'eq', value: memberId },
          { field: 'eventType', operator: 'eq', value: 'APPOINTMENT' }
        ], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'eventDateTime', dir: 'desc' }
      ],
      take: 99999
    };

    this.state.filter.filters.push({ field: 'eventDateTime', operator: 'gte', value: fromDate.toISOString().replace('Z', '') });
    this.state.filter.filters.push({ field: 'eventDateTime', operator: 'lte', value: toDate.toISOString().replace('Z', '') });

    this.eventListService.queryByStateAndSource(this.state, 'patientSummary');
  }
}
